// S14 — System Settings yönetim sayfası.
//
// BE bize key-value tabanlı bir AppSetting tablosu sunuyor. Her anahtar
// kendi `category` etiketi, `valueType` (String/Int/Bool/Json) ve opsiyonel
// `rowVersion` (concurrency) ile geliyor. Burada anahtarları kategoriye göre
// gruplayıp her satırı inline düzenliyoruz; "secret/password/token/key"
// kelimelerini içeren ya da `isPublic=false + valueType=String + name match`
// olan anahtarları hassas kabul edip Kaydet'ten önce onay penceresi
// açıyoruz.

import { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Grid,
  Input,
  InputNumber,
  Row,
  Segmented,
  Select,
  Skeleton,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Lock, Save, ShieldAlert, Eye, EyeOff, Plus } from 'lucide-react';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { PageHeader } from '@shared/components/PageHeader';
import { ROUTES } from '@shared/constants/routes';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { useConfirm } from '@shared/components/ConfirmDialog';
import { settingsApi } from '@api/settings';
import {
  isSensitiveKey,
  categoryOf,
  type AppSetting,
  type SettingValueType,
} from '@features/settings/types';
import { intConstraint, settingValueError } from '@features/settings/constraints';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface DraftValue {
  value: string;
  valueType: SettingValueType;
  isPublic: boolean;
  description: string | null;
  category: string;
}

function defaultDraft(s: AppSetting): DraftValue {
  return {
    value: s.value,
    valueType: s.valueType,
    isPublic: s.isPublic,
    description: s.description,
    category: s.category,
  };
}

const CATEGORY_KEYS = new Set([
  'auth',
  'cache',
  'mail',
  'storage',
  'site',
  'rate',
  'security',
  'feature',
  'integration',
  'general',
]);

function categoryLabel(cat: string, td: (key: string) => string): string {
  const lower = cat.toLowerCase();
  if (CATEGORY_KEYS.has(lower)) return td(`categories.${lower}`);
  return cat.charAt(0).toUpperCase() + cat.slice(1);
}

function SettingValueEditor({
  settingKey,
  draft,
  reveal,
  sensitive,
  onChange,
}: {
  settingKey: string;
  draft: DraftValue;
  reveal: boolean;
  sensitive: boolean;
  onChange: (next: DraftValue) => void;
}) {
  switch (draft.valueType) {
    case 'Bool':
      return (
        <Switch
          checked={draft.value === 'true' || draft.value === '1'}
          onChange={(c) => onChange({ ...draft, value: c ? 'true' : 'false' })}
        />
      );
    case 'Int': {
      const { min, max } = intConstraint(settingKey);
      return (
        <InputNumber
          value={draft.value === '' ? null : Number(draft.value)}
          min={min}
          max={max}
          step={1}
          precision={0}
          onChange={(v) => onChange({ ...draft, value: v == null ? '' : String(v) })}
          style={{ width: 200 }}
        />
      );
    }
    case 'Json':
      return (
        <Input.TextArea
          value={draft.value}
          autoSize={{ minRows: 3, maxRows: 8 }}
          onChange={(e) => onChange({ ...draft, value: e.target.value })}
          spellCheck={false}
          style={{ fontFamily: 'var(--ds-font-mono, ui-monospace, monospace)' }}
        />
      );
    default:
      return sensitive && !reveal ? (
        <Input.Password
          value={draft.value}
          onChange={(e) => onChange({ ...draft, value: e.target.value })}
          visibilityToggle={false}
        />
      ) : (
        <Input
          value={draft.value}
          onChange={(e) => onChange({ ...draft, value: e.target.value })}
        />
      );
  }
}

function SettingRow({
  setting,
  onSaved,
}: {
  setting: AppSetting;
  onSaved: () => void;
}) {
  const { t } = useTranslation(['settings', 'common', 'validation', 'errors']);
  const screens = useBreakpoint();
  const isNarrow = !screens.md;
  const confirm = useConfirm();
  const [draft, setDraft] = useState<DraftValue>(defaultDraft(setting));
  const [reveal, setReveal] = useState(false);
  const sensitive = isSensitiveKey(setting.key);

  const dirty = useMemo(() => {
    return (
      draft.value !== setting.value ||
      draft.valueType !== setting.valueType ||
      draft.isPublic !== setting.isPublic ||
      (draft.description ?? '') !== (setting.description ?? '') ||
      draft.category !== setting.category
    );
  }, [draft, setting]);

  const valueError = useMemo(
    () => settingValueError(setting.key, draft.valueType, draft.value),
    [setting.key, draft.valueType, draft.value],
  );
  const tr = t as unknown as (key: string, opts?: Record<string, number>) => string;
  const errorText = valueError ? tr(valueError.key, valueError.values) : null;

  const updateMut = useMutation({
    mutationFn: () =>
      settingsApi.update(setting.key, {
        value: draft.value,
        valueType: draft.valueType,
        category: draft.category,
        isPublic: draft.isPublic,
        description: draft.description,
        rowVersion: setting.rowVersion,
      }),
    onSuccess: () => {
      void message.success(t('toast.updated', { key: setting.key }));
      onSaved();
    },
    onError: (err) => {
      void message.error(resolveApiErrorMessage(err));
    },
  });

  const handleSave = async () => {
    if (valueError) return;
    if (sensitive) {
      const ok = await confirm({
        title: t('sensitiveConfirm.title'),
        description: t('sensitiveConfirm.description', { key: setting.key }),
        okText: t('sensitiveConfirm.okText'),
        cancelText: t('common:actions.cancel'),
        danger: true,
      });
      if (!ok) return;
    }
    updateMut.mutate();
  };

  return (
    <div
      style={{
        padding: '12px 16px',
        borderRadius: 8,
        background: 'var(--ds-surface-1)',
        border: '1px solid var(--ds-border)',
        marginBottom: 12,
      }}
      data-testid={`setting-row-${setting.key}`}
    >
      <Row gutter={[12, 12]} align="middle">
        <Col xs={24} md={8}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Text strong style={{ wordBreak: 'break-all' }}>
              {setting.key}
            </Text>
            {sensitive ? (
              <Tooltip title={t('row.sensitiveTooltip')}>
                <Lock size={12} />
              </Tooltip>
            ) : null}
            {setting.isPublic ? (
              <Tag color="blue" style={{ marginInlineStart: 0 }}>
                {t('row.publicTag')}
              </Tag>
            ) : null}
          </div>
          {setting.description ? (
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
              {setting.description}
            </Text>
          ) : null}
        </Col>
        <Col xs={24} md={10}>
          <Space.Compact style={{ width: '100%' }}>
            <div style={{ flex: 1 }}>
              <SettingValueEditor
                settingKey={setting.key}
                draft={draft}
                reveal={reveal}
                sensitive={sensitive}
                onChange={setDraft}
              />
            </div>
            {sensitive && draft.valueType === 'String' ? (
              <Button
                onClick={() => setReveal((v) => !v)}
                aria-label={reveal ? t('row.hideValue') : t('row.revealValue')}
                icon={reveal ? <EyeOff size={14} /> : <Eye size={14} />}
              />
            ) : null}
          </Space.Compact>
          {errorText ? (
            <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              {errorText}
            </Text>
          ) : null}
        </Col>
        <Col xs={12} md={3}>
          <Select<SettingValueType>
            value={draft.valueType}
            onChange={(v) => setDraft({ ...draft, valueType: v })}
            options={[
              { value: 'String', label: t('valueType.String') },
              { value: 'Int', label: t('valueType.Int') },
              { value: 'Bool', label: t('valueType.Bool') },
              { value: 'Json', label: t('valueType.Json') },
            ]}
            style={{ width: '100%' }}
            size={isNarrow ? 'middle' : 'small'}
          />
        </Col>
        <Col xs={12} md={3} style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            size="small"
            icon={<Save size={14} />}
            disabled={!dirty || !!valueError}
            loading={updateMut.isPending}
            onClick={() => void handleSave()}
            data-testid={`save-${setting.key}`}
          >
            {t('common:actions.save')}
          </Button>
        </Col>
      </Row>
    </div>
  );
}

function CreateSettingForm({ onCreated }: { onCreated: () => void }) {
  const { t } = useTranslation(['settings', 'common', 'validation', 'errors']);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<{
    key: string;
    value: string;
    valueType: SettingValueType;
    category: string;
    isPublic: boolean;
    description?: string;
  }>();

  const createMut = useMutation({
    mutationFn: (input: {
      key: string;
      value: string;
      valueType: SettingValueType;
      category: string;
      isPublic: boolean;
      description?: string;
    }) =>
      settingsApi.create({
        key: input.key,
        value: input.value,
        valueType: input.valueType,
        category: input.category,
        isPublic: input.isPublic,
        description: input.description ?? null,
      }),
    onSuccess: () => {
      void message.success(t('toast.created'));
      form.resetFields();
      setOpen(false);
      onCreated();
    },
    onError: (err) => void message.error(resolveApiErrorMessage(err)),
  });

  if (!open) {
    return (
      <Button icon={<Plus size={14} />} onClick={() => setOpen(true)}>
        {t('create.openButton')}
      </Button>
    );
  }

  return (
    <Card size="small" style={{ marginBottom: 16 }} title={t('create.cardTitle')}>
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) => createMut.mutate(v)}
        initialValues={{ valueType: 'String', category: 'general', isPublic: false }}
      >
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item
              name="key"
              label={t('create.keyLabel')}
              rules={[{ required: true, message: t('create.keyRequired') }]}
            >
              <Input placeholder={t('create.keyPlaceholder')} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="value"
              label={t('create.valueLabel')}
              rules={[{ required: true, message: t('create.valueRequired') }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={12} md={4}>
            <Form.Item name="valueType" label={t('create.typeLabel')}>
              <Select
                options={[
                  { value: 'String', label: t('valueType.String') },
                  { value: 'Int', label: t('valueType.Int') },
                  { value: 'Bool', label: t('valueType.Bool') },
                  { value: 'Json', label: t('valueType.Json') },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={12} md={4}>
            <Form.Item name="category" label={t('create.categoryLabel')}>
              <Input placeholder={t('create.categoryPlaceholder')} />
            </Form.Item>
          </Col>
          <Col xs={12} md={4}>
            <Form.Item name="isPublic" label={t('create.publicLabel')} valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="description" label={t('create.descriptionLabel')}>
              <Input.TextArea rows={2} />
            </Form.Item>
          </Col>
        </Row>
        <Space>
          <Button type="primary" htmlType="submit" loading={createMut.isPending}>
            {t('create.submit')}
          </Button>
          <Button onClick={() => setOpen(false)}>{t('common:actions.cancel')}</Button>
        </Space>
      </Form>
    </Card>
  );
}

export function SettingsPage() {
  const { t } = useTranslation(['settings', 'common', 'validation', 'errors']);
  const td = t as unknown as (key: string) => string;
  const canManage = useCan(PERMISSIONS.SETTINGS_MANAGE);
  const qc = useQueryClient();
  const [filter, setFilter] = useState<'all' | 'public' | 'private' | 'sensitive'>('all');
  const [search, setSearch] = useState('');

  const settingsQuery = useQuery({
    queryKey: ['settings', 'list'],
    queryFn: () => settingsApi.list(),
  });

  const settingsData = settingsQuery.data;
  const all = useMemo(() => settingsData ?? [], [settingsData]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return all.filter((s) => {
      if (filter === 'public' && !s.isPublic) return false;
      if (filter === 'private' && s.isPublic) return false;
      if (filter === 'sensitive' && !isSensitiveKey(s.key)) return false;
      if (term && !s.key.toLowerCase().includes(term) && !(s.description ?? '').toLowerCase().includes(term))
        return false;
      return true;
    });
  }, [all, filter, search]);

  const grouped = useMemo(() => {
    const map = new Map<string, AppSetting[]>();
    for (const s of filtered) {
      const cat = categoryOf(s);
      const arr = map.get(cat) ?? [];
      arr.push(s);
      map.set(cat, arr);
    }
    return Array.from(map.entries())
      .map(([cat, items]) => ({ cat, items: items.sort((a, b) => a.key.localeCompare(b.key)) }))
      .sort((a, b) => a.cat.localeCompare(b.cat));
  }, [filtered]);

  const handleSaved = () => {
    void qc.invalidateQueries({ queryKey: ['settings'] });
  };

  const breadcrumb = [
    { label: t('breadcrumb.dashboard'), to: ROUTES.DASHBOARD },
    { label: t('breadcrumb.settings') },
  ];

  if (!canManage) {
    return (
      <div>
        <PageHeader
          title={t('breadcrumb.settings')}
          description={t('page.noAccessTitle')}
          breadcrumb={breadcrumb}
        />
        <Card>
          <Alert
            type="error"
            showIcon
            icon={<ShieldAlert size={18} />}
            message={t('page.accessDeniedTitle')}
            description={t('page.accessDeniedDescription')}
          />
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="settings-page">
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        breadcrumb={breadcrumb}
      />

      <Card>
        <Space wrap style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
          <Space wrap>
            <Input.Search
              placeholder={t('filter.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              style={{ width: 280 }}
            />
            <Segmented<typeof filter>
              value={filter}
              onChange={(v) => setFilter(v)}
              options={[
                { value: 'all', label: t('filter.all', { total: all.length }) },
                { value: 'public', label: t('filter.public') },
                { value: 'private', label: t('filter.private') },
                {
                  value: 'sensitive',
                  label: (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Lock size={12} /> {t('filter.sensitive')}
                    </span>
                  ),
                },
              ]}
            />
          </Space>
          <CreateSettingForm onCreated={handleSaved} />
        </Space>

        {settingsQuery.isLoading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : grouped.length === 0 ? (
          <Alert type="info" showIcon message={t('list.noMatch')} />
        ) : (
          <Collapse
            defaultActiveKey={grouped.map((g) => g.cat)}
            items={grouped.map((g) => ({
              key: g.cat,
              label: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <strong>{categoryLabel(g.cat, td)}</strong>
                  <Badge count={g.items.length} color="blue" showZero={false} />
                </span>
              ),
              children: g.items.map((s) => (
                <SettingRow key={s.id || s.key} setting={s} onSaved={handleSaved} />
              )),
            }))}
          />
        )}
      </Card>
    </div>
  );
}
